<template>
  <div class="products-page">
    <!-- Main Content -->
    <section class="main-content">
      <div class="content-container">
        <!-- Breadcrumb -->
        <div class="breadcrumb">
          <router-link to="/portal" class="breadcrumb-link">首页</router-link>
          <Icon icon="lucide:chevron-right" :size="14" color="#9CA3AF" />
          <span class="breadcrumb-current">产品中心</span>
        </div>

        <!-- Header -->
        <div class="page-header">
          <h1 class="page-title">产品中心</h1>
          <p class="page-sub">17款产品，覆盖建站营销、电商交易、AI智能、内容社区全场景</p>
        </div>

        <!-- Filter Bar -->
        <div class="filter-bar">
          <div class="filter-tabs">
            <button
              v-for="cat in categories"
              :key="cat"
              class="filter-btn"
              :class="{ active: activeCategory === cat }"
              @click="activeCategory = cat"
            >
              {{ cat }}
            </button>
          </div>
          <div class="filter-spacer"></div>
          <div class="filter-search">
            <Icon icon="lucide:search" :size="16" color="#9CA3AF" class="search-icon" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="搜索产品..."
              class="search-input"
            />
          </div>
        </div>

        <!-- Result Count -->
        <p class="result-count">共找到 {{ filteredProducts.length }} 款产品</p>

        <!-- Product Grid -->
        <div class="product-grid">
          <div
            v-for="product in filteredProducts"
            :key="product.id"
            class="product-card"
            @click="goToDetail(product.id)"
          >
            <div
              class="card-header"
              :style="{ background: `linear-gradient(135deg, ${product.bgColor}, #fff)` }"
            >
              <span class="card-tag" :style="{ background: `rgba(255,255,255,.8)` }">
                {{ product.category }}
              </span>
              <div class="card-icon-wrap" :style="{ boxShadow: `0 8px 24px ${product.color}25` }">
                <Icon :icon="product.icon" :size="36" :color="product.color" />
              </div>
            </div>
            <div class="card-body">
              <h3 class="card-title">{{ product.name }}</h3>
              <p class="card-desc">{{ product.desc }}</p>
              <div class="card-features">
                <span
                  v-for="feature in product.features?.slice(0, 3)"
                  :key="feature"
                  class="feature-tag"
                  :style="{ background: product.bgColor, color: product.color }"
                >
                  {{ feature }}
                </span>
              </div>
              <div class="card-footer">
                <div class="card-actions">
                  <button class="btn-primary-sm" @click.stop="showTrialModal">免费试用</button>
                  <button class="btn-outline-sm" @click.stop="showTrialModal">联系销售</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { Icon } from '@/components/Icon'
import { products } from '../data/portalData'
import { usePortalState } from '../usePortalState'
import { getPortalProductCatalog } from '../data/portalCatalog'

defineOptions({ name: 'ProductsPage' })

const router = useRouter()
const { showTrialModal } = usePortalState()

const categories = [
  '全部',
  '零售电商',
  '智能制造',
  '教育培训',
  '餐饮服务',
  '医疗健康',
  '金融保险',
  '房产建筑',
  '文化传媒',
  '旅游出行',
  '物流供应链',
  '政务公共',
  '科技互联网'
]
const activeCategory = ref('全部')
const searchQuery = ref('')
const displayProducts = ref(products)

onMounted(async () => {
  try {
    const catalog = await getPortalProductCatalog()
    if (catalog.length) displayProducts.value = catalog
  } catch {
    // CMS 未配置或暂不可用时保留当前门户内容。
  }
})

const filteredProducts = computed(() => {
  let list = displayProducts.value
  if (activeCategory.value !== '全部') {
    list = list.filter((p) => p.industries?.includes(activeCategory.value))
  }
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase()
    list = list.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.desc.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
    )
  }
  return list
})

function goToDetail(id: string) {
  router.push(`/portal/products/${id}`)
}
</script>

<style lang="scss" scoped>
.products-page {
  // navbar-spacer handled by Login.vue
}

.main-content {
  background: #f9fafb;
  padding: 48px 24px 80px;
  min-height: calc(100vh - 64px);
}

.content-container {
  max-width: 1280px;
  margin: 0 auto;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 24px;

  .breadcrumb-link {
    cursor: pointer;
    color: #9ca3af;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: #0066ff;
    }
  }

  .breadcrumb-current {
    color: #374151;
  }
}

.page-header {
  margin-bottom: 32px;

  .page-title {
    font-size: 36px;
    font-weight: 800;
    margin-bottom: 8px;
  }

  .page-sub {
    font-size: 16px;
    color: #6b7280;
  }
}

.filter-bar {
  position: sticky;
  top: 64px;
  z-index: 50;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .filter-tabs {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .filter-spacer {
    flex: 1;
  }

  .filter-btn {
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    background: #f3f4f6;
    color: #4b5563;

    &.active {
      background: #0066ff;
      color: #fff;
    }

    &:hover:not(.active) {
      background: #e5e7eb;
    }
  }

  .filter-search {
    position: relative;

    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
    }

    .search-input {
      width: 200px;
      height: 36px;
      padding: 0 12px 0 36px;
      border: 1px solid #e5e7eb;
      border-radius: 20px;
      font-size: 13px;
      outline: none;
      transition: border-color 0.2s;

      &:focus {
        border-color: #0066ff;
      }
    }
  }
}

.result-count {
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 16px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.product-card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .card-header {
    height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
  }

  .card-tag {
    position: absolute;
    top: 12px;
    left: 12px;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.8);
  }

  .card-icon-wrap {
    width: 72px;
    height: 72px;
    background: #fff;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-body {
    padding: 20px;
  }

  .card-title {
    font-size: 17px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .card-desc {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 12px;
    line-height: 1.6;
  }

  .card-features {
    margin-bottom: 16px;
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  .feature-tag {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .card-price {
    font-size: 14px;
    font-weight: 600;
    color: #0066ff;
  }

  .card-actions {
    display: flex;
    gap: 8px;
  }

  .btn-primary-sm {
    height: 32px;
    font-size: 12px;
    padding: 0 14px;
    background: #0066ff;
    color: #fff;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #0044cc;
    }
  }

  .btn-outline-sm {
    height: 32px;
    font-size: 12px;
    padding: 0 14px;
    background: #fff;
    color: #4b5563;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #0066ff;
      color: #0066ff;
    }
  }
}

@media (max-width: 1024px) {
  .product-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .product-grid {
    grid-template-columns: 1fr;
  }

  .filter-bar {
    flex-direction: column;
    align-items: stretch;

    .filter-spacer {
      display: none;
    }

    .filter-search {
      .search-input {
        width: 100%;
      }
    }
  }

  .page-title {
    font-size: 28px;
  }
}
</style>
